// "Send feedback": a button that opens a short form (what it is about, a rating, the message) and sends it to the
// site's team with where it came from (the page, the project, the run on screen). Used first by the DFT module's
// pilot; admins read it in Admin -> Feedback.
import { Button, Group, Modal, Rating, SegmentedControl, Stack, Text, Textarea } from "@mantine/core";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { post } from "../lib/api";
import { ErrorBox, notifyOk } from "./Errors";

export const FEEDBACK_KINDS = [
  { value: "results", label: "Results" }, { value: "bug", label: "Something broke" },
  { value: "usability", label: "Hard to use" }, { value: "idea", label: "Idea" }, { value: "other", label: "Other" },
];

export function FeedbackButton({ product, context, label = "Send feedback" }: {
  product: string; context?: Record<string, string | undefined>; label?: string;
}) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("results");
  const [rating, setRating] = useState(0);
  const [message, setMessage] = useState("");
  const send = useMutation({
    mutationFn: () => post("/feedback", { product, kind, rating: rating || null, message,
      context: { page: window.location.pathname, ...Object.fromEntries(Object.entries(context ?? {}).filter(([, v]) => v)) } }),
    onSuccess: () => {
      setOpen(false); setMessage(""); setRating(0);
      notifyOk("Thank you - your feedback has been sent to the team.");
    },
  });
  return (
    <>
      <Button size="xs" variant="subtle" onClick={() => setOpen(true)} data-testid="feedback-open">{label}</Button>
      <Modal opened={open} onClose={() => setOpen(false)} title="Send feedback" size="lg">
        <Stack gap="sm" data-testid="feedback-form">
          <Text size="xs" c="dimmed">It goes to the site's team with this page{context?.job_id ? " and the run on screen" : ""}.
            Please say what you did, what you expected and what happened.</Text>
          <SegmentedControl size="xs" value={kind} onChange={setKind} data={FEEDBACK_KINDS} />
          <Group gap="xs"><Text size="sm">How useful is it so far?</Text>
            <Rating value={rating} onChange={setRating} aria-label="rating" /></Group>
          <Textarea label="Message" autosize minRows={4} maxRows={12} maxLength={4000} value={message}
            onChange={(e) => setMessage(e.currentTarget.value)} />
          <ErrorBox error={send.error} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => send.mutate()} disabled={!message.trim()} loading={send.isPending} data-testid="feedback-send">Send</Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
